import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Collapse,
  useTheme
} from '@mui/material';
import { useThemeMode } from '../../common/contexts/ThemeContext';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CloseIcon from '@mui/icons-material/Close';

/**
 * CopilotChat Component
 * 
 * A collapsible AI assistant chat panel that appears on the right side of the page.
 * Styled consistently with the dark theme and gradient accents from the homepage.
 */
const CopilotChat: React.FC = () => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Array<{ text: string; isUser: boolean }>>([
    {
      text: 'Welcome to the Iris Co-Pilot. What strategic decisions are you facing today? Let\'s cut months of planning down to minutes.',
      isUser: false
    }
  ]);

  const suggestedPrompts = [
    'Recommend an optimal consolidation strategy for my two clinics',
    'Show me my top 3 competitor threats and the best counter-move'
  ];

  const handlePromptClick = (prompt: string) => {
    setInput(prompt);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    setMessages([...messages, { text: input, isUser: true }]);
    setInput('');
  };

  const gradientText = {
    background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
  } as const;

  return (
    <Box
      sx={{
        position: 'fixed',
        right: 0,
        top: 64, // Below AppBar
        bottom: 0,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.3s ease'
      }}
    >
      {/* Collapsed Button (Always Visible) */}
      {isMinimized && (
        <IconButton
          onClick={() => {
            setIsMinimized(false);
            setIsOpen(true);
          }}
          sx={{
            position: 'absolute',
            right: 16,
            bottom: 16,
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: theme.palette.background.paper,
            border: isDark ? '1px solid rgba(0,180,255,0.35)' : '1px solid rgba(0,180,255,0.25)',
            boxShadow: isDark ? '0 8px 24px rgba(0,180,255,0.25)' : '0 8px 24px rgba(0,180,255,0.15)',
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: 'rgba(0,180,255,0.15)',
              boxShadow: '0 12px 32px rgba(0,180,255,0.35)',
              transform: 'scale(1.05)'
            },
            transition: 'all 0.25s ease'
          }}
        >
          <SmartToyIcon />
        </IconButton>
      )}

      {/* Chat Panel */}
      <Collapse in={!isMinimized} orientation="horizontal">
        <Paper
          elevation={8}
          sx={{
            width: 400,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.background.paper,
            borderLeft: isDark ? '1px solid rgba(138,43,226,0.3)' : '1px solid rgba(138,43,226,0.2)',
            boxShadow: isDark ? '-8px 0 32px rgba(0,0,0,0.5)' : '-8px 0 32px rgba(0,0,0,0.2)'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: isDark ? '1px solid rgba(138,43,226,0.3)' : '1px solid rgba(138,43,226,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: isDark 
                ? 'linear-gradient(180deg, rgba(11,11,11,0.95), rgba(0,0,0,0.95))'
                : 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(245,245,247,0.95))'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SmartToyIcon sx={{ color: '#000', fontSize: 20 }} />
              </Box>
              <Typography
                sx={{
                  ...gradientText,
                  fontWeight: 700,
                  fontSize: '1.1rem'
                }}
              >
                AI Assistant
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setIsMinimized(true)}
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': { color: theme.palette.primary.main, backgroundColor: 'rgba(0,180,255,0.15)' }
                }}
                title="Minimize"
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  setIsMinimized(true);
                  setIsOpen(false);
                }}
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': { color: '#ff1744', backgroundColor: 'rgba(255,23,68,0.15)' }
                }}
                title="Close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Messages Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'
              },
              '&::-webkit-scrollbar-thumb': {
                background: isDark ? 'rgba(138,43,226,0.5)' : 'rgba(138,43,226,0.3)',
                borderRadius: '4px',
                '&:hover': {
                  background: isDark ? 'rgba(138,43,226,0.7)' : 'rgba(138,43,226,0.5)'
                }
              }
            }}
          >
            <List sx={{ p: 0 }}>
              {messages.map((message, index) => (
                <ListItem
                  key={index}
                  sx={{
                    justifyContent: message.isUser ? 'flex-end' : 'flex-start',
                    px: 0,
                    py: 0.5
                  }}
                >
                  <Paper
                    elevation={2}
                    sx={{
                      p: 1.5,
                      maxWidth: '75%',
                      borderRadius: 2,
                      backgroundColor: message.isUser
                        ? 'rgba(0,180,255,0.15)'
                        : 'rgba(138,43,226,0.15)',
                      border: message.isUser
                        ? '1px solid rgba(0,180,255,0.3)'
                        : '1px solid rgba(138,43,226,0.3)',
                      boxShadow: message.isUser
                        ? '0 4px 12px rgba(0,180,255,0.15)'
                        : '0 4px 12px rgba(138,43,226,0.15)'
                    }}
                  >
                    <Typography
                      sx={{
                        color: theme.palette.text.primary,
                        fontSize: '0.9rem',
                        lineHeight: 1.5,
                        wordBreak: 'break-word'
                      }}
                    >
                      {message.text}
                    </Typography>
                  </Paper>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Suggested Prompts - Only show when input is empty and no user messages */}
          {!input.trim() && messages.filter(m => m.isUser).length === 0 && (
            <Box
              sx={{
              px: 2,
              pb: 1.5,
              borderTop: isDark ? '1px solid rgba(138,43,226,0.2)' : '1px solid rgba(138,43,226,0.15)',
              backgroundColor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)'
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: theme.palette.text.secondary,
                  mb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600
                }}
              >
                Suggested Prompts
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {suggestedPrompts.map((prompt, idx) => (
                  <Box
                    key={idx}
                    onClick={() => handlePromptClick(prompt)}
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      border: isDark ? '1px solid rgba(138,43,226,0.2)' : '1px solid rgba(138,43,226,0.15)',
                      backgroundColor: isDark ? 'rgba(138,43,226,0.05)' : 'rgba(138,43,226,0.03)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: isDark ? 'rgba(0,180,255,0.4)' : 'rgba(0,180,255,0.3)',
                        backgroundColor: isDark ? 'rgba(0,180,255,0.1)' : 'rgba(0,180,255,0.08)',
                        transform: 'translateX(2px)'
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        color: theme.palette.text.secondary,
                        lineHeight: 1.4,
                        fontStyle: 'italic',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        overflow: 'visible'
                      }}
                    >
                      "{prompt}"
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Input Area */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: 2,
              borderTop: isDark ? '1px solid rgba(138,43,226,0.3)' : '1px solid rgba(138,43,226,0.2)',
              display: 'flex',
              gap: 1,
              backgroundColor: isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.03)'
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? 'rgba(11,11,11,0.8)' : 'rgba(255,255,255,0.8)',
                  color: theme.palette.text.primary,
                  border: isDark ? '1px solid rgba(138,43,226,0.3)' : '1px solid rgba(138,43,226,0.2)',
                  '&:hover': {
                    borderColor: isDark ? 'rgba(0,180,255,0.5)' : 'rgba(0,180,255,0.4)'
                  },
                  '&.Mui-focused': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: isDark ? '0 0 8px rgba(0,180,255,0.3)' : '0 0 8px rgba(0,180,255,0.2)'
                  },
                  '& fieldset': {
                    border: 'none'
                  }
                },
                '& .MuiInputBase-input': {
                  color: theme.palette.text.primary,
                  '&::placeholder': {
                    color: theme.palette.text.secondary,
                    opacity: 1
                  }
                }
              }}
            />
            <IconButton
              type="submit"
              disabled={!input.trim()}
              sx={{
                backgroundColor: input.trim() ? 'rgba(0,180,255,0.2)' : (isDark ? 'rgba(138,43,226,0.1)' : 'rgba(138,43,226,0.05)'),
                border: input.trim()
                  ? (isDark ? '1px solid rgba(0,180,255,0.4)' : '1px solid rgba(0,180,255,0.3)')
                  : (isDark ? '1px solid rgba(138,43,226,0.2)' : '1px solid rgba(138,43,226,0.15)'),
                color: input.trim() ? theme.palette.primary.main : theme.palette.text.disabled,
                '&:hover': input.trim()
                  ? {
                      backgroundColor: 'rgba(0,180,255,0.3)',
                      boxShadow: isDark ? '0 4px 12px rgba(0,180,255,0.25)' : '0 4px 12px rgba(0,180,255,0.15)',
                      transform: 'scale(1.05)'
                    }
                  : {},
                transition: 'all 0.25s ease'
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default CopilotChat;

